import { useState } from 'react';
import { ProfileOutlined, SearchOutlined } from '@ant-design/icons';

import { DataTable } from '../../../../components/shared';
import { Empty, Select, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { ngayGioVN, soVN } from '../../../../lib/dinh-dang';
import { useKetQuaCao } from '../../hooks/useLichSu';
import type { KetQuaCao } from '../../types';
import { LoiRpa } from '../chung';

/**
 * Kết quả cào dạng thẻ (`scrape-results.js` native) Robot gửi theo `executionId` của lượt. Native chỉ có API, chưa có
 * màn; ở đây hiện thành bảng, lọc theo nhãn ở máy khách (tối đa 500 dòng mới nhất).
 */
export function TabKetQuaCao({ executionId }: { executionId: string }) {
  const { t, locale } = useT();
  const q = useKetQuaCao(executionId);
  const [nhan, setNhan] = useState('');

  if (!executionId) return <Empty icon={ProfileOutlined} title={t('rpaDuLieu.caoKhongCoMa')} className="py-8" />;
  if (q.isPending) return <Skeleton className="h-48 w-full" />;
  if (q.isError) return <LoiRpa loi={q.error} onThuLai={() => void q.refetch()} />;

  const ds = q.data;
  const cacNhan = [...new Set(ds.map((r) => r.tag).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'vi'));
  const dong = nhan ? ds.filter((r) => r.tag === nhan) : ds;
  const tieuDe = t('rpaDuLieu.tab.scrape');

  if (ds.length === 0) {
    return <Empty icon={ProfileOutlined} title={t('rpaDuLieu.caoRong')} description={t('rpaDuLieu.caoRongMoTa')} className="py-8" />;
  }
  return (
    <div className="min-w-0 space-y-3">
      {cacNhan.length > 1 && (
        <Select<string>
          value={nhan}
          onChange={setNhan}
          aria-label={t('rpaDuLieu.caoLocNhan')}
          className="w-56"
          options={[{ value: '', label: t('rpaDuLieu.caoMoiNhan') }, ...cacNhan.map((n) => ({ value: n, label: n }))]}
        />
      )}
      {dong.length === 0 ? (
        <Empty icon={SearchOutlined} title={t('rpaDuLieu.rongLoc')} className="py-6" />
      ) : (
        <DataTable<KetQuaCao>
          caption={tieuDe}
          size="small"
          rowKey={(r) => `${r.executionId}:${r.position}`}
          dataSource={dong}
          pagination={dong.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
          columns={[
            { title: t('rpaDuLieu.caoViTri'), key: 'position', width: 72, render: (_, r) => <span className="tabular-nums">{soVN(r.position, locale)}</span> },
            { title: t('rpaDuLieu.caoNhan'), key: 'tag', width: 140, render: (_, r) => r.tag },
            {
              title: t('rpaDuLieu.caoNoiDung'),
              key: 'text',
              render: (_, r) => (
                <div className="min-w-0 max-w-md">
                  <p className="whitespace-pre-wrap break-words text-content-primary">{r.text}</p>
                  {r.sub && <p className="break-words text-xs text-content-muted">{r.sub}</p>}
                </div>
              ),
            },
            {
              title: t('rpaDuLieu.caoNguon'),
              key: 'sourceUrl',
              render: (_, r) => <span className="block max-w-xs truncate text-xs" title={r.sourceUrl}>{r.sourceUrl}</span>,
            },
            {
              title: t('rpaDuLieu.caoLuc'),
              key: 'scrapedAt',
              width: 150,
              render: (_, r) => <span className="whitespace-nowrap text-xs tabular-nums">{ngayGioVN(r.scrapedAt, locale)}</span>,
            },
          ]}
        />
      )}
    </div>
  );
}
